import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown } from 'lucide-react';

const faqData = [
  {
    question: 'What is Connect Band?',
    answer: 'Connect Band is a screenless wearable created for a more thoughtful kind of connection. It is designed to help individuals, couples, families, and loved ones stay closer through wellness, awareness, and peace of mind — without adding another screen to everyday life.'
  },
  {
    question: 'Which Connect plan is right for me?',
    answer: 'Connect One is for personal wellness. Connect Duo is for two people who want a more connected lifestyle. Connect Family is for households and families who want shared awareness and peace of mind in one experience. Connect Care is for families who want a more care-centered way to stay close to parents, seniors, or loved ones.'
  },
  {
    question: 'Can Connect Family include parents too?',
    answer: 'Yes. Connect Family is designed to include the people who matter most, whether that means children, parents, spouses, or grandparents. Connect Care is not the only plan for parents — it is simply a more care-focused experience for families who want that added layer of support.'
  },
  {
    question: 'Is the app available yet?',
    answer: 'The Connect experience is currently in development. Early-access members will be the first to receive updates, previews, and launch information as the product continues to take shape.'
  },
  {
    question: 'What happens after I reserve?',
    answer: 'Once you join early access, you\'ll receive confirmation and be among the first to hear about launch progress, product previews, and any early supporter benefits made available before release.'
  },
  {
    question: 'Is privacy important to Connect?',
    answer: 'Yes. Privacy and trust are fundamental to the Connect vision. Because Connect is built around close relationships and shared awareness, protecting that trust is an essential part of the experience.'
  }
];

const FAQItem = ({ question, answer, isOpen, onClick, index }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4, delay: index * 0.05 }}
      className="border-b border-black/5 last:border-b-0"
    >
      <button
        onClick={onClick}
        className="w-full py-5 md:py-6 flex items-center justify-between gap-4 text-left group"
        data-testid={`faq-question-${index}`}
      >
        <span className="text-base md:text-lg font-medium text-[#1A1A1A] group-hover:text-[#333] transition-colors pr-4">
          {question}
        </span>
        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.3 }}
          className="flex-shrink-0"
        >
          <ChevronDown className="w-5 h-5 text-[#7A7A7A]" />
        </motion.div>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <p className="pb-5 md:pb-6 text-[#5A5A5A] leading-relaxed pr-12">
              {answer}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section id="faq" className="py-16 md:py-24 section-soft" data-testid="faq-section">
      <div className="container-custom">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-10 md:mb-14"
          >
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-semibold text-[#1A1A1A] mb-3">
              Questions & Answers
            </h2>
            <p className="text-[#5A5A5A] text-base md:text-lg">
              Everything you need to know about Connect
            </p>
          </motion.div>

          {/* FAQ Items */}
          <div className="bg-white rounded-3xl border border-black/5 px-6 md:px-10 shadow-sm">
            {faqData.map((faq, index) => (
              <FAQItem
                key={index}
                question={faq.question}
                answer={faq.answer}
                isOpen={openIndex === index}
                onClick={() => toggleFAQ(index)}
                index={index}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
